import React, { useEffect, useState } from 'react';
import {
    Checkbox, Form, Input, Row, Space, Table, message, Modal, Select, Button, Radio, Tabs,
    List, Card, Alert, Popconfirm, Typography, InputNumber
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import Paragraph from 'antd/lib/typography/Paragraph';
import { TextService } from '../../Services/TextService';
import { useAuth } from '../../Contexts/AuthContext';
import LeftMenuSettings from './LeftSettings';
import { ColumnsType } from 'antd/lib/table';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';
import { BookyButton } from '../../Components/Buttons/BookyButton';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import {
    WebApiModelsMemberField, WebApiModelsView, WebApiViewDtoCreateViewDto,
    WebApiViewDtoUpdateViewDto, WebApiViewDtoCreateViewColumnDto,
    WebApiModelsEnumerationTypesMemberFieldTypes, WebApiCustomListsDtoCustomListDto,
    WebApiModelsCustomListField
} from '../../Repository/eventbookyapi';
import { BookySelectDropDown } from '../../Components/Forms/BookySelectDropDown';
import { CustomListService } from '../../Services/CustomListService';
import { UI_CONTROLS } from '../../Types/FieldTypes';
import { MemberFieldService } from '../../Services/MemberFieldService';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import AddCustomFieldModal from '../MemberSettings/AddCustomFieldModal';
import { CustomField } from '../../Types/CustomField';
import AddFieldModal from '../MemberSettings/AddFieldModal';
import { getViewColumnFields, getViewColumnLabel } from '../../Helpers/ViewColumnHelper';
import { getFieldText } from '../../Helpers/MemberFieldHelper';
import { LookupRelationType } from '../../Types/LookupRelationType';
import { ViewService } from '../../Services/ViewService';
import { CustomListFieldService } from '../../Services/CustomLifFieldService';
import LoadingSpinner from '../../Components/Shared/LoadingSpinner';
import { sortAlphabetically, sortDate } from '../../Helpers/TableSortHelper';
import { ViewType } from '../../Types/ViewType';

const { Option } = Select;

interface ViewColumn {
    sortOrder: number;
    columnType?: WebApiModelsEnumerationTypesMemberFieldTypes;
    customFieldId?: number | null;
    fieldName?: string;
}

const MemberFormSettings: React.FC = () => {
    const { user } = useAuth();
    const GetText = TextService.GetText;

    // --- MemberSettings state ---
    const [fields, setFields] = useState<(WebApiModelsMemberField | CustomField)[]>([]);
    const [allCustomLists, setAllCustomLists] = useState<any[]>([]);
    const [addFieldModal, setAddFieldModal] = useState(false);
    const [addSystemFieldModal, setAddSystemFieldModal] = useState(false);
    const [memberForm] = Form.useForm();

    const refreshFields = async () => {
        const updated = await MemberFieldService.GetMemberFields();
        updated.sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
        setFields(updated);
    };

    const saveFields = async () => {
        await MemberFieldService.SaveMemberFields({
            fields: fields.filter((f): f is WebApiModelsMemberField => !!f),
        });
        message.success(GetText('EventBranding_SuccessSaved'));
        const updated = await MemberFieldService.GetMemberFields();
        updated.sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
        setFields(updated);
    };

    const isVisibleLockedPublic = (f: WebApiModelsMemberField) =>
        [MemberFieldTypes.FirstName, MemberFieldTypes.LastName, MemberFieldTypes.Address,
         MemberFieldTypes.PostalArea, MemberFieldTypes.PostalCode, MemberFieldTypes.PhoneNumber,
         MemberFieldTypes.Email].includes(f.columnType as any);

    const isVisibleLockedAdmin = (f: WebApiModelsMemberField) =>
        f.columnType === MemberFieldTypes.UniqueId || isVisibleLockedPublic(f);

    const isMandatoryLocked = (f: WebApiModelsMemberField) => f.columnType === MemberFieldTypes.UniqueId;
    const isHelpTextDisabled = (f: WebApiModelsMemberField) =>
        f.columnType === MemberFieldTypes.UniqueId || f.columnType === MemberFieldTypes.PhoneNumber;
    const isExtraField = (f: WebApiModelsMemberField) =>
        [MemberFieldTypes.Extra1, MemberFieldTypes.Extra2, MemberFieldTypes.Extra3].includes(f.columnType as any);

    const updateField = (updated: WebApiModelsMemberField) => {
        setFields(prev => prev.map(f => ('id' in f && f.id === updated.id ? updated : f)));
    };

    const updateSortOrder = (index: number, newValue: any) => {
        const newIndex = newValue - 1;
        if (newIndex < 0 || newIndex >= fields.length) return;
        const reordered = [...fields];
        const [moved] = reordered.splice(index, 1);
        reordered.splice(newIndex, 0, moved);
        reordered.forEach((f, i) => { f.columnOrder = i + 1; });
        setFields(reordered);
    };

    const availableSortOrder = Array.from({ length: fields.length }, (_, i) => ({ label: i + 1, value: i + 1 }));

    const memberColumns: ColumnsType<WebApiModelsMemberField | CustomField> = [
        {
            title: GetText('MemberFields_FieldName'), key: 'name', width: '50px',
            render: (_, field: any) => {
                if (field.columnType === MemberFieldTypes.Custom)
                    return <Input maxLength={100} onChange={e => updateField({ ...field, nameAdmin: e.target.value, namePublic: e.target.value })} value={field.nameAdmin ?? ''} />;
                // Only here: spell out that the membership field is the fee, to tell it apart from groups etc.
                if (field.columnType === MemberFieldTypes.Membership)
                    return GetText('MemberFormSettings_MembershipFieldName');
                return 'columnType' in field ? getFieldText(field.columnType!) : '';
            },
        },
        {
            title: GetText('MemberFields_FieldSortOrder'), key: 'sortOrder', width: '30px',
            render: (_, field: any, index) => 'columnOrder' in field
                ? <BookySelectDropDown style={{ width: '80px' }} handleChange={(v: any) => updateSortOrder(index, v)} value={field.columnOrder ?? undefined} options={availableSortOrder} />
                : null,
        },
        {
            title: GetText('MemberFields_FieldNameVisible'), key: 'nameVisible', width: '50px',
            render: (_, field: any) => 'nameAdmin' in field
                ? <Input maxLength={100} disabled={!isExtraField(field)} onChange={e => updateField({ ...field, nameAdmin: e.target.value })} value={field.nameAdmin ?? ''} />
                : null,
        },
        {
            title: GetText('MemberFields_VisibleInAdmin'), key: 'visibleAdmin', width: '25px',
            render: (_, field: any) => 'visibleInAdmin' in field
                ? <Checkbox disabled={isVisibleLockedAdmin(field)} onChange={e => updateField({ ...field, visibleInAdmin: e.target.checked })} checked={field.visibleInAdmin} />
                : null,
        },
        {
            title: GetText('MemberFields_VisibleInPublic'), key: 'visiblePublic', width: '50px',
            render: (_, field: any) => 'visiblePublic' in field
                ? <Checkbox disabled={isVisibleLockedPublic(field)} onChange={e => updateField({ ...field, visiblePublic: e.target.checked })} checked={field.visiblePublic} />
                : null,
        },
        {
            title: GetText('MemberFields_MandatoryInAdmin'), key: 'mandatoryAdmin', width: '50px',
            render: (_, field: any) => 'mandatoryAdmin' in field
                ? <Checkbox disabled={isMandatoryLocked(field)} onChange={e => updateField({ ...field, mandatoryAdmin: e.target.checked })} checked={field.mandatoryAdmin} />
                : null,
        },
        {
            title: GetText('MemberFields_MandatoryInPublic'), key: 'mandatoryPublic', width: '50px',
            render: (_, field: any) => 'mandatoryPublic' in field
                ? <Checkbox disabled={isMandatoryLocked(field)} onChange={e => updateField({ ...field, mandatoryPublic: e.target.checked })} checked={field.mandatoryPublic} />
                : null,
        },
        {
            title: GetText('MemberFields_HelpTextPublic'), dataIndex: 'helpTextPublic', key: 'helpText', width: '200px',
            render: (_, field: any) => 'helpTextPublic' in field
                ? <Input disabled={isHelpTextDisabled(field)} onChange={e => updateField({ ...field, helpTextPublic: e.target.value })} value={field.helpTextPublic ?? ''} />
                : null,
        },
        {
            title: 'UI-kontroll', key: 'uiControl', width: '80px',
            render: (_, field: any) => field.columnType === MemberFieldTypes.Custom && 'uiControl' in field
                ? <Select value={field.uiControl} size="small" style={{ width: 110 }} onChange={v => setFields(prev => prev.map(f => ((f as any).id === field.id ? { ...f, uiControl: v } : f)))}>
                    {UI_CONTROLS.map(t => <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>)}
                  </Select>
                : null,
        },
        {
            title: 'Alternativ', key: 'alternatives', width: '120px',
            render: (_, field: any) => field.columnType === MemberFieldTypes.Custom
                ? <Input value={field.alternatives ?? ''} size="small" onChange={e => setFields(prev => prev.map(f => ((f as any).id === field.id ? { ...f, alternatives: e.target.value } : f)))} />
                : null,
        },
        {
            title: 'Kopplingstabell', key: 'customListId', width: '120px',
            render: (_, field: any) => field.columnType === MemberFieldTypes.Custom
                ? <Select value={field.customListId} size="small" style={{ width: 120 }} allowClear placeholder="Välj lista" onChange={v => setFields(prev => prev.map(f => ((f as any).id === field.id ? { ...f, customListId: v } : f)))}>
                    {allCustomLists.map(l => <Select.Option key={l.id} value={l.id}>{l.name}</Select.Option>)}
                  </Select>
                : null,
        },
        {
            title: GetText('MemberFields_LookupRelationType'), key: 'lookupRelationType', width: '140px',
            render: (_, field: any) => field.columnType === MemberFieldTypes.Custom && field.customListId
                ? <Select value={field.lookupRelationType ?? LookupRelationType.None} size="small" style={{ width: 130 }} onChange={v => setFields(prev => prev.map(f => ((f as any).id === field.id ? { ...f, lookupRelationType: v } : f)))}>
                    <Select.Option value={LookupRelationType.None}>{GetText('MemberFields_LookupRelationType_None')}</Select.Option>
                    <Select.Option value={LookupRelationType.OneToOne}>{GetText('MemberFields_LookupRelationType_OneToOne')}</Select.Option>
                    <Select.Option value={LookupRelationType.OneToMany}>{GetText('MemberFields_LookupRelationType_OneToMany')}</Select.Option>
                    <Select.Option value={LookupRelationType.ManyToMany}>{GetText('MemberFields_LookupRelationType_ManyToMany')}</Select.Option>
                  </Select>
                : null,
        },
        {
            title: '', key: 'remove', width: '60px',
            render: (_, field: any, index) => !isVisibleLockedAdmin(field)
                ? <Button danger size="small" onClick={() => Modal.confirm({
                    title: GetText('MemberFieldSettings_RemoveFieldTitle'),
                    content: GetText('MemberFieldSettings_RemoveFieldConfirm'),
                    okText: GetText('Common_Yes'),
                    cancelText: GetText('Common_Abort'),
                    onOk: () => {
                        const updated = [...fields];
                        updated.splice(index, 1);
                        updated.forEach((f, i) => { f.columnOrder = i + 1; });
                        setFields(updated);
                    },
                })}>{GetText('MemberFieldSettings_RemoveField')}</Button>
                : null,
        },
    ];

    // --- Views state ---
    const [views, setViews] = useState<WebApiModelsView[]>([]);
    const [viewsLoading, setViewsLoading] = useState(false);
    const [viewsLoaded, setViewsLoaded] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingView, setEditingView] = useState<WebApiModelsView | null>(null);
    const [memberFieldsForViews, setMemberFieldsForViews] = useState<WebApiModelsMemberField[]>([]);
    const [viewCustomLists, setViewCustomLists] = useState<WebApiCustomListsDtoCustomListDto[]>([]);
    const [customListFields, setCustomListFields] = useState<WebApiModelsCustomListField[]>([]);
    const [selectedViewType, setSelectedViewType] = useState<number>(ViewType.Members);
    const [selectedCustomListId, setSelectedCustomListId] = useState<number | null>(null);
    const [viewColumns, setViewColumns] = useState<ViewColumn[]>([]);
    const [viewForm] = Form.useForm();

    const ViewTypeOptions = [
        { value: ViewType.Members, label: GetText('Views_ViewTypeMember') },
        { value: ViewType.CustomList, label: GetText('Views_ViewTypeCustomList') },
        { value: ViewType.PublicSiteMembers, label: GetText('Views_ViewTypePublicSiteMembers') || 'Public Site Members' },
    ];

    const loadViews = async () => {
        setViewsLoading(true);
        try {
            setViews(await ViewService.GetAllViews());
            setViewsLoaded(true);
        } catch {
            message.error(GetText('Views_FailedToLoad'));
        }
        setViewsLoading(false);
    };

    const loadMemberFieldsForViews = async () => {
        try {
            const apiFields = await MemberFieldService.GetMemberFields();
            const standardFields: WebApiModelsMemberField[] = [{
                id: 17, columnType: MemberFieldTypes.Group as any,
                nameAdmin: GetText('MemberFields_Group'), namePublic: GetText('MemberFields_Group'),
                visibleInAdmin: true, visiblePublic: false,
            } as WebApiModelsMemberField];
            setMemberFieldsForViews([...standardFields, ...apiFields]);
        } catch {
            message.error(GetText('Views_FailedToLoadFields'));
        }
    };

    const loadViewCustomLists = async () => {
        try {
            setViewCustomLists(await CustomListService.GetCustomLists());
        } catch {
            message.error(GetText('Views_FailedToLoadCustomLists') || 'Failed to load custom lists');
        }
    };

    const loadCustomListFields = async (listId: number) => {
        try {
            setCustomListFields(await CustomListFieldService.GetCCustomListFields(listId));
        } catch {
            message.error(GetText('Views_FailedToLoadCustomListFields') || 'Failed to load custom list fields');
        }
    };

    const handleViewCreate = () => {
        setEditingView(null);
        setViewColumns([]);
        setSelectedViewType(ViewType.Members);
        setSelectedCustomListId(null);
        setCustomListFields([]);
        viewForm.resetFields();
        setModalVisible(true);
    };

    const handleViewEdit = (view: WebApiModelsView) => {
        setEditingView(view);
        setSelectedViewType(view.viewType || 0);
        setSelectedCustomListId(view.customListId || null);
        viewForm.setFieldsValue({ name: view.name, viewType: view.viewType, customListId: view.customListId });
        if (view.viewType === ViewType.CustomList && view.customListId) loadCustomListFields(view.customListId);
        const cols: ViewColumn[] = (view.viewColumns || []).map(col => ({
            sortOrder: col.sortOrder || 0,
            columnType: col.columnType,
            customFieldId: col.customFieldId,
            fieldName: getViewColumnLabel(view.viewType, col.columnType as any, memberFieldsForViews, col.customFieldId) || undefined,
        })).sort((a, b) => a.sortOrder - b.sortOrder);
        setViewColumns(cols);
        setModalVisible(true);
    };

    const handleViewDelete = async (viewId: number) => {
        try {
            await ViewService.DeleteView(viewId);
            message.success(GetText('Views_DeletedSuccessfully'));
            loadViews();
        } catch {
            message.error(GetText('Views_FailedToDelete'));
        }
    };

    const handleViewTypeChange = (value: number) => {
        setSelectedViewType(value);
        setViewColumns([]);
        viewForm.setFieldsValue({ customListId: undefined });
        setSelectedCustomListId(null);
        setCustomListFields([]);
    };

    const handleCustomListChange = async (value: number) => {
        setSelectedCustomListId(value);
        setViewColumns([]);
        if (value) await loadCustomListFields(value);
        else setCustomListFields([]);
    };

    const handleAddViewColumn = (fieldId: number) => {
        let fieldName: string | undefined;
        let columnType: any;
        let customFieldId: number | null = null;
        if (selectedViewType === ViewType.CustomList) {
            const f = customListFields.find(f => f.id === fieldId);
            if (!f) return;
            fieldName = f.nameAdmin || undefined;
            columnType = MemberFieldTypes.Custom;
            customFieldId = fieldId;
        } else {
            const f = getViewColumnFields(selectedViewType, memberFieldsForViews).find(f => f.id === fieldId);
            if (!f) return;
            fieldName = getViewColumnLabel(selectedViewType, f.columnType as any, memberFieldsForViews, f.id) ?? undefined;
            columnType = f.columnType;
            customFieldId = f.columnType === MemberFieldTypes.Custom ? fieldId : null;
        }
        setViewColumns(prev => [...prev, { sortOrder: prev.length, columnType, customFieldId, fieldName }]);
    };

    const handleRemoveViewColumn = (index: number) => {
        setViewColumns(prev => { const n = prev.filter((_, i) => i !== index); n.forEach((c, i) => c.sortOrder = i); return n; });
    };

    const handleMoveViewColumn = (index: number, direction: 'up' | 'down') => {
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === viewColumns.length - 1)) return;
        const n = [...viewColumns];
        const t = direction === 'up' ? index - 1 : index + 1;
        [n[index], n[t]] = [n[t], n[index]];
        n.forEach((c, i) => c.sortOrder = i);
        setViewColumns(n);
    };

    const handleViewSubmit = async (values: any) => {
        try {
            if (values.viewType === ViewType.PublicSiteMembers) {
                const existing = views.find(v => v.viewType === ViewType.PublicSiteMembers && (!editingView || v.id !== editingView.id));
                if (existing) { message.error(GetText('Views_PublicSiteMembersAlreadyExists') || 'A Public Site Members view already exists.'); return; }
            }
            const cols: WebApiViewDtoCreateViewColumnDto[] = viewColumns.map(c => ({ sortOrder: c.sortOrder, columnType: c.columnType, customFieldId: c.customFieldId }));
            if (editingView) {
                await ViewService.UpdateView(editingView.id!, { name: values.name, viewType: values.viewType, customListId: values.customListId || null, viewColumns: cols } as WebApiViewDtoUpdateViewDto);
                message.success(GetText('Views_UpdatedSuccessfully'));
            } else {
                await ViewService.CreateView({ name: values.name, viewType: values.viewType, customListId: values.customListId || null, viewColumns: cols } as WebApiViewDtoCreateViewDto);
                message.success(GetText('Views_CreatedSuccessfully'));
            }
            setModalVisible(false);
            viewForm.resetFields();
            setViewColumns([]);
            loadViews();
        } catch {
            message.error(GetText('Views_FailedToSave'));
        }
    };

    const viewTableColumns = [
        { title: GetText('Views_ColumnID'), dataIndex: 'id', key: 'id', width: 80 },
        { title: GetText('Views_ColumnName'), dataIndex: 'name', key: 'name', width: 250, sorter: (a: WebApiModelsView, b: WebApiModelsView) => sortAlphabetically(a, b, 'name') },
        {
            title: GetText('Views_ColumnCustomList'), dataIndex: 'customListId', key: 'customListId', width: 180,
            render: (id: number | null) => { if (!id) return '-'; return viewCustomLists.find(cl => cl.id === id)?.name || `ID: ${id}`; },
        },
        {
            title: GetText('Views_ColumnViewType'), dataIndex: 'viewType', key: 'viewType', width: 150,
            render: (vt: number) => ViewTypeOptions.find(o => o.value === vt)?.label || `Type ${vt}`,
            filters: ViewTypeOptions.map(o => ({ text: o.label, value: o.value })),
            onFilter: (value: boolean | React.Key, record: WebApiModelsView) => record.viewType === value,
        },
        {
            title: GetText('Views_ColumnCreatedAt'), dataIndex: 'createdAt', key: 'createdAt', width: 180,
            render: (d: Date) => d ? new Date(d).toLocaleString() : '-',
            sorter: (a: WebApiModelsView, b: WebApiModelsView) => sortDate(a.createdAt, b.createdAt),
        },
        {
            title: GetText('Views_ColumnUpdatedAt'), dataIndex: 'updatedAt', key: 'updatedAt', width: 180,
            render: (d: Date | null) => d ? new Date(d).toLocaleString() : '-',
        },
        {
            title: GetText('Views_ColumnActions'), key: 'actions', width: 150, fixed: 'right' as const,
            render: (_: any, record: WebApiModelsView) => (
                <Space size="middle">
                    <EditOutlined style={{ fontSize: '20px', cursor: 'pointer', color: '#1890ff' }} onClick={() => handleViewEdit(record)} />
                    <Popconfirm title={GetText('Views_DeleteConfirmTitle')} description={GetText('Views_DeleteConfirmDescription')} onConfirm={() => handleViewDelete(record.id!)} okText={GetText('Common_Yes')} cancelText={GetText('Common_No')}>
                        <DeleteOutlined style={{ color: 'red', fontSize: '20px', cursor: 'pointer' }} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const onTabChange = (key: string) => {
        if (key === 'views' && !viewsLoaded) {
            loadViews();
            loadMemberFieldsForViews();
            loadViewCustomLists();
        }
    };

    // MemberSettings loads on mount
    useEffect(() => {
        MemberFieldService.GetMemberFields().then(result => {
            result.sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0));
            setFields(result);
        });
        (async () => {
            try {
                const lists = await CustomListService.GetCustomLists();
                setAllCustomLists(lists);
            } catch {}
        })();
    }, []);

    useEffect(() => {
        if (user) {
            // language side-effect kept for compatibility
        }
    }, [user]);

    const pageHelpContent = <>
        <Paragraph><b>{GetText('MemberFieldSettings_InfoTitle')}</b></Paragraph>
        <ul>
            <li>{GetText('MemberFieldSettings_InfoAdmin')}</li>
            <li>{GetText('MemberFieldSettings_InfoPublic')}</li>
            <li>{GetText('MemberFieldSettings_InfoMandatory')}</li>
            <li>{GetText('MemberFieldSettings_InfoHelpText')}</li>
            <li>{GetText('MemberFieldSettings_InfoOrder')}</li>
            <li>{GetText('MemberFieldSettings_InfoCustomFields')}</li>
        </ul>
        <Paragraph><b>{GetText('Views_InfoTitle')}</b></Paragraph>
        <ul>
            <li>{GetText('Views_InfoDescription')}</li>
            <li>{GetText('Views_InfoTypes')}</li>
        </ul>
    </>;

    const tabItems = [
        {
            key: 'memberSettings',
            label: GetText('MemberFieldSettings_Title'),
            children: (
                <>
                    <Table pagination={false} columns={memberColumns} rowKey="Id" dataSource={[...fields].sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0))} />
                    <Space style={{ marginTop: '20px' }}>
                        <BookyButton type="primary" onClick={saveFields} text={GetText('AddMember_Ok')} />
                        <BookyButton type="default" onClick={() => setAddSystemFieldModal(true)} text={GetText('MemberFieldSettings_AddField')} />
                        <BookyButton disabled={(user?.subscriptionLevel ?? 0) < SubscriptionLevel.Premium} type="default" onClick={() => setAddFieldModal(true)} text={GetText('MemberFieldSettings_AddCustomField')} />
                        {(user?.subscriptionLevel ?? 0) < SubscriptionLevel.Premium && <span><a href='/Pricing'>Uppgradera till premium</a> för att lägga fler fält</span>}
                    </Space>
                    <AddFieldModal open={addSystemFieldModal} onCancel={() => setAddSystemFieldModal(false)} GetText={GetText} onFieldAdded={async () => { await refreshFields(); setAddSystemFieldModal(false); }} />
                    <AddCustomFieldModal open={addFieldModal} onCancel={() => setAddFieldModal(false)} onFieldAdded={async () => { await refreshFields(); setAddFieldModal(false); }} allCustomLists={allCustomLists} GetText={GetText} />
                </>
            ),
        },
        {
            key: 'views',
            label: GetText('Views_Title'),
            children: viewsLoading
                ? <LoadingSpinner loadingText={GetText('Views_LoadingText')} />
                : (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleViewCreate}>{GetText('Views_AddNewView')}</Button>
                        </div>
                        <Table columns={viewTableColumns} dataSource={views} rowKey="id" scroll={{ x: 1200 }} pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` }} />
                        <Modal
                            title={editingView ? GetText('Views_ModalTitleEdit') : GetText('Views_ModalTitleCreate')}
                            open={modalVisible}
                            onCancel={() => { setModalVisible(false); viewForm.resetFields(); setViewColumns([]); }}
                            onOk={() => viewForm.submit()}
                            okText={editingView ? GetText('Views_ButtonUpdate') : GetText('Views_ButtonCreate')}
                            cancelText={GetText('Views_ButtonCancel')}
                            width={800}
                        >
                            <Form form={viewForm} layout="vertical" onFinish={handleViewSubmit}>
                                <Form.Item label={GetText('Views_LabelViewName')} name="name" rules={[{ required: true, message: GetText('Views_ValidationViewNameRequired') }, { min: 2, message: GetText('Views_ValidationViewNameMin') }]}>
                                    <Input placeholder={GetText('Views_PlaceholderViewName')} />
                                </Form.Item>
                                <Form.Item label={GetText('Views_LabelViewType')} name="viewType" rules={[{ required: true, message: GetText('Views_ValidationViewTypeRequired') }]} initialValue={ViewType.Members}>
                                    <Select style={{ width: '100%' }} placeholder={GetText('Views_PlaceholderViewType')} onChange={handleViewTypeChange}>
                                        {ViewTypeOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                                    </Select>
                                </Form.Item>
                                {selectedViewType === ViewType.PublicSiteMembers && (
                                    <Alert message={GetText('Views_PublicSiteMembersInfoTitle') || 'Member Search Enabled'} description={GetText('Views_PublicSiteMembersInfoDescription') || 'By creating this view, you will enable your members to search for other members when they are logged in.'} type="info" showIcon style={{ marginBottom: 16 }} />
                                )}
                                {selectedViewType === ViewType.CustomList && (
                                    <Form.Item label={GetText('Views_LabelCustomListId')} name="customListId" help={GetText('Views_HelpCustomListId')} rules={[{ required: true, message: GetText('Views_ValidationCustomListRequired') || 'Please select a custom list' }]}>
                                        <Select style={{ width: '100%' }} placeholder={GetText('Views_PlaceholderCustomListId')} onChange={handleCustomListChange}>
                                            {viewCustomLists.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                                        </Select>
                                    </Form.Item>
                                )}
                                <Card title={GetText('Views_CardTitleViewColumns')} size="small" style={{ marginTop: 16 }}>
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {selectedViewType === ViewType.CustomList && !selectedCustomListId ? (
                                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>{GetText('Views_SelectCustomListFirst') || 'Please select a custom list first'}</div>
                                        ) : (
                                            <>
                                                <Select style={{ width: '100%' }} placeholder={GetText('Views_PlaceholderSelectField')} onChange={handleAddViewColumn} value={undefined} disabled={selectedViewType === ViewType.CustomList && !selectedCustomListId}>
                                                    {selectedViewType === ViewType.CustomList
                                                        ? customListFields.map(f => <Option key={f.id} value={f.id}>{f.nameAdmin}</Option>)
                                                        : getViewColumnFields(selectedViewType, memberFieldsForViews).map(f => <Option key={f.id} value={f.id}>{getViewColumnLabel(selectedViewType, f.columnType as any, memberFieldsForViews, f.id)}</Option>)
                                                    }
                                                </Select>
                                                {viewColumns.length > 0 ? (
                                                    <List size="small" bordered dataSource={viewColumns} renderItem={(col, i) => (
                                                        <List.Item actions={[
                                                            <Button key="up" size="small" icon={<ArrowUpOutlined />} onClick={() => handleMoveViewColumn(i, 'up')} disabled={i === 0} />,
                                                            <Button key="down" size="small" icon={<ArrowDownOutlined />} onClick={() => handleMoveViewColumn(i, 'down')} disabled={i === viewColumns.length - 1} />,
                                                            <Button key="del" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveViewColumn(i)} />,
                                                        ]}>
                                                            <Space><span style={{ fontWeight: 'bold' }}>{i + 1}.</span><span>{col.fieldName}</span><span style={{ color: '#999' }}>({GetText('Views_ColumnOrderLabel')}: {col.sortOrder})</span></Space>
                                                        </List.Item>
                                                    )} />
                                                ) : (
                                                    <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>{GetText('Views_NoColumnsText')}</div>
                                                )}
                                            </>
                                        )}
                                    </Space>
                                </Card>
                            </Form>
                        </Modal>
                    </>
                ),
        },
    ];

    return (
        <LayoutLeftMenu title={GetText('MemberFieldSettings_Title')} pageHelpContent={pageHelpContent} menu={<LeftMenuSettings selected="memberFormSettings" />}>
            <Tabs defaultActiveKey="memberSettings" items={tabItems} onChange={onTabChange} />
        </LayoutLeftMenu>
    );
};

export default MemberFormSettings;
